import { IoSparklesSharp } from "react-icons/io5";
import { LiaHourglassHalfSolid } from "react-icons/lia";
import { AiOutlineFileProtect } from "react-icons/ai";

import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import CardLoader from "@/ui/CardLoader";
import { MdWavingHand } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";
import { useGetRecommendations } from "../features/home/useGetRecommendations";
import {
  formatDate,
  toPascalCase,
  truncateStringToMaxWords,
} from "../utils/helpers";
import PageLayout from "../ui/PageLayout";
import { Badge } from "../components/ui/badge";
import LeadsButton from "../ui/LeadsButton";
import { Button } from "../components/ui/button";
import {
  Calendar,
  DeleteIcon,
  FileText,
  Heading2,
  Landmark,
} from "lucide-react";
import { useGetLeadsContracts } from "../features/leads/useGetLeadsContracts";
import Heading from "../ui/Heading";
import { useUserMetadata } from "../hooks/useUserMetadata";
import { RiDeleteBin5Line } from "react-icons/ri";
import { BsHourglassSplit } from "react-icons/bs";
import { useRemoveContractLeads } from "../features/leads/useRemoveContractLeads";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

function WelcomeMessage({}) {
  const { data: userMetadata } = useUserMetadata();
  const getWelcomeMessage = () => {
    const date = new Date();
    const hour = date.getHours();

    if (hour <= 10) {
      return (
        <div className="mb-10 scroll-m-20 text-4xl font-bold tracking-tight lg:text-5xl bg-clip-text text-transparent bg-gradient-to-r from-gray-900 via-sky-500 to-gray-200 dark:from-gray-200 dark:via-sky-500 dark:to-gray-900 border-b border-gray-200 dark:border-gray-800 pb-2">
          Good Morning {userMetadata?.first_name ? userMetadata.first_name : ""}
        </div>
      );
    } else if (hour > 10 && hour < 17) {
      return (
        <div className="mb-10 scroll-m-20 text-4xl font-bold tracking-tight lg:text-5xl bg-clip-text text-transparent bg-gradient-to-r from-gray-900 via-sky-500 to-gray-200 dark:from-gray-200 dark:via-sky-500 dark:to-gray-900 border-b border-gray-200 dark:border-gray-800 pb-2">
          Good Afternoon{" "}
          {userMetadata?.first_name ? userMetadata.first_name : ""}
        </div>
      );
    } else {
      return (
        <div className="mb-10 scroll-m-20 text-4xl font-bold tracking-tight lg:text-5xl bg-clip-text text-transparent bg-gradient-to-r from-gray-900 via-sky-500 to-gray-200 dark:from-gray-200 dark:via-sky-500 dark:to-gray-900 border-b border-gray-200 dark:border-gray-800 pb-2">
          Good Evening {userMetadata?.first_name ? userMetadata.first_name : ""}
        </div>
      );
    }
  };

  const welcomeMessage = getWelcomeMessage();
  return welcomeMessage;
}

function ProposalCard({ metadata, id }) {
  const navigate = useNavigate();

  return (
    <div>
      <Card className="w-full h-full">
        <CardHeader className="flex items-start">
          <LeadsButton
            proposalId={id}
            searchType="federal"
            className="ml-auto"
          />
          <CardTitle
            onClick={() => navigate(`/contract/federal/${id}`)}
            className="hover:cursor-pointer hover:underline"
          >
            {toPascalCase(metadata.Title)}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {truncateStringToMaxWords(metadata.Summary, 100)}
        </CardContent>
        <CardFooter className="flex flex-col items-start gap-1">
          {metadata?.["Department/Ind.Agency"] && (
            <Badge variant="secondary" className="flex gap-1">
              <Landmark className="h-3 w-3"></Landmark>
              {toPascalCase(metadata["Department/Ind.Agency"])}
            </Badge>
          )}
          <div className="flex gap-1 flex-wrap w-full">
            {metadata?.Type && (
              <Badge variant="secondary" className="flex gap-1">
                {" "}
                <FileText className="h-3 w-3"></FileText>{" "}
                {toPascalCase(metadata.Type)}
              </Badge>
            )}
            {metadata?.SetASideCode && (
              <Badge variant="secondary" className="flex  gap-1">
                <AiOutlineFileProtect className="h-3 w-3"></AiOutlineFileProtect>
                {metadata.SetASideCode}
              </Badge>
            )}
            <Badge variant="secondary" className="flex  ml-auto">
              {metadata?.ResponseDeadLine && (
                <div className="flex gap-1 items-center">
                  <Calendar className="h-3 w-3"></Calendar>
                  {formatDate(metadata.ResponseDeadLine)}
                </div>
              )}
            </Badge>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}

function SavedOpportunityCard({ savedOpportunity, idLeads, searchType }) {
  const { mutate } = useRemoveContractLeads();
  const [timeLeft, setTimeLeft] = useState(0);
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const due = new Date(
        savedOpportunity?.ResponseDeadLine || savedOpportunity?.dueDate
      );
      const timeDifference = due.getTime() - now.getTime();
      const daysDifference = Math.ceil(timeDifference / (1000 * 3600 * 24));

      if (daysDifference <= 0) {
        setIsExpired(true);
        setTimeLeft("0");
      } else {
        setTimeLeft(daysDifference);
      }
    };
    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 3600000);

    return () => clearInterval(timer);
  }, [savedOpportunity?.ResponseDeadLine, savedOpportunity?.dueDate]);

  const navigate = useNavigate();
  return (
    <Card className="w-full">
      <CardHeader className="flex flex-wrap justify-between w-full">
        <div className="flex justify-between gap-3">
          <CardTitle
            onClick={() => navigate(`/contract/${searchType}/${idLeads}`)}
            className="hover:cursor-pointer hover:underline"
          >
            {toPascalCase(savedOpportunity?.Title || savedOpportunity?.title)}
          </CardTitle>
          <div
            variant="link"
            className="text-primary whitespace-nowrap gap-1 inline-flex h-7 justify-between items-center hover:cursor-default"
          >
            <Badge
              variant="secondary"
              className={`flex ${
                timeLeft <= 0 && "text-foreground/50 bg-secondary"
              } ml-auto`}
            >
              <div className="flex gap-1 items-center">
                {timeLeft > 0 ? (
                  <>
                    <LiaHourglassHalfSolid className="h-5 w-5" />
                    {timeLeft} {timeLeft === 1 ? "day left" : "days left"}
                  </>
                ) : savedOpportunity?.ResponseDeadLine ||
                  savedOpportunity?.dueDate ? (
                  "Expired"
                ) : (
                  "Date unavailable"
                )}
              </div>
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardFooter className="flex justify-end ">
        {isExpired && (
          <div className="flex gap-2">
            <Button
              onClick={() => mutate({ proposalId: idLeads, searchType })}
              variant="outline"
              className="flex items-center h-7 p-2 text-destructive/70 border-destructive/70 hover:text-destructive"
            >
              <RiDeleteBin5Line className="w-4 h-4" />
            </Button>
          </div>
        )}
      </CardFooter>
    </Card>
  );
}

function Home() {
  const { data: recommendations, isLoading: recommendationsLoading } =
    useGetRecommendations();
  const { data: leads, isLoading: leadsLoading } = useGetLeadsContracts();
  const searchTypes = ["federal", "sled", "grant", "sbir"];
  const hasSavedOpportunities = searchTypes.some(
    (type) => leads?.[type] && leads[type].length > 0
  );

  return (
    <div>
      <Heading as="h1">
        <WelcomeMessage />
      </Heading>
      <Heading as="h2" className={"flex items-center gap-2"}>
        Featured For You <IoSparklesSharp className="w-9 h-9 text-primary/70" />
      </Heading>
      {recommendationsLoading ? (
        <div className="mt-4">
          {Array.from({ length: 1 }).map((_, index) => (
            <CardLoader className="h-96" key={index} />
          ))}
        </div>
      ) : (
        <>
          {recommendations?.length > 0 ? (
            <Carousel>
              <CarouselContent>
                {recommendations.map((proposal, index) => (
                  <CarouselItem key={proposal.id}>
                    <ProposalCard
                      metadata={proposal.metadata}
                      id={proposal.id}
                    />
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious />
              <CarouselNext />
            </Carousel>
          ) : (
            <p>
              Update company profile to get opportunities that match your
              profile
            </p>
          )}
        </>
      )}
      {hasSavedOpportunities && (
        <>
          <Heading as="h2" className="mt-5">
            Your Saved Opportunities
          </Heading>
          {leadsLoading ? (
            <div className="mt-4 flex-wrap">
              {Array.from({ length: 3 }).map((_, index) => (
                <CardLoader className="h-96" key={index} />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              {searchTypes.map((type) =>
                (leads?.[type] || []).map((savedOpportunity) => (
                  <SavedOpportunityCard
                    key={savedOpportunity.NoticeId || savedOpportunity.id}
                    idLeads={savedOpportunity.NoticeId || savedOpportunity.id}
                    savedOpportunity={savedOpportunity}
                    searchType={type}
                  />
                ))
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Home;
